import React, { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';

const InstallPrompt = lazy(() => import('./components/shared/pwa/InstallPrompt').then(m => ({ default: m.InstallPrompt })));
const PWAInstallBanner = lazy(() => import('./components/shared/pwa/PWAInstallBanner').then(m => ({ default: m.PWAInstallBanner })));
const OfflineIndicator = lazy(() => import('./components/shared/pwa/OfflineIndicator').then(m => ({ default: m.OfflineIndicator })));
const PWAStatus = lazy(() => import('./components/shared/pwa/PWAStatus').then(m => ({ default: m.PWAStatus })));
const PWAUpdateNotification = lazy(() => import('./components/shared/pwa/PWAUpdateNotification').then(m => ({ default: m.PWAUpdateNotification })));
const UpdatePrompt = lazy(() => import('./components/shared/pwa/UpdatePrompt').then(m => ({ default: m.UpdatePrompt })));
const PlatformDisclaimer = lazy(() => import('./components/shared/PlatformDisclaimer').then(m => ({ default: m.PlatformDisclaimer })));
const EnhancedGDPRBanner = lazy(() => import('./components/shared/EnhancedGDPRBanner').then(m => ({ default: m.EnhancedGDPRBanner })));
const QuickActionsWidget = lazy(() => import('./components/pwa/QuickActionsWidget').then(m => ({ default: m.QuickActionsWidget })));
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Converter } from './pages/Converter';
import { Education } from './pages/Education';
import { EducationHub } from './pages/EducationHub';
import { NairaJourney } from './pages/NairaJourney';
import { Terms } from './pages/legal/Terms';
import { Privacy } from './pages/legal/Privacy';
import { Disclaimer } from './pages/legal/Disclaimer';
import { Blog } from './pages/Blog';
import { BlogPost } from './pages/BlogPost';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { UserDashboard } from './components/user/UserDashboard';
import { AuthGuard } from './components/auth/AuthGuard';
import { AutoContentGenerator } from './components/blog/AutoContentGenerator';
import { usePWA } from './hooks/usePWA';
import { initializeGA } from './utils/analytics/gtag';
import { useAnalytics } from './hooks/useAnalytics';
import { EnhancedErrorBoundary } from './utils/security/error-boundary';
import { useOfflineSupport } from './hooks/useOfflineSupport';
import { realTimeClient } from './services/websocket/real-time-client';
import { enhancedDB } from './utils/storage/enhanced-indexeddb';

function App() {
  const { needRefresh, updateServiceWorker, closePrompt } = usePWA();
  const { isOnline, hasOfflineData } = useOfflineSupport();
  
  useEffect(() => {
    initializeGA();

    // Initialize enhanced features
    enhancedDB.initialize().catch(console.error);

    // Defer real-time connection to avoid blocking initial load
    const timer = setTimeout(() => {
      realTimeClient.connect().catch(console.error);
    }, 2000);

    return () => {
      clearTimeout(timer);
      realTimeClient.disconnect();
    };
  }, []);

  useAnalytics(); // Track page views

  return (
    <EnhancedErrorBoundary>
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/converter" element={<Converter />} />
            <Route path="/education" element={<Education />} />
            <Route path="/education-hub" element={<EducationHub />} />
            <Route path="/education/path/:pathId" element={<EducationHub />} />
            <Route path="/education/module/:moduleId" element={<EducationHub />} />
            <Route path="/education/*" element={<Navigate to="/education-hub" replace />} />
            <Route path="/naira-journey" element={<NairaJourney />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/blog/category/:category" element={<Blog />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route
              path="/dashboard"
              element={
                <AuthGuard>
                  <UserDashboard />
                </AuthGuard>
              }
            />
          </Routes>
        </main>
        <Footer />
        <AutoContentGenerator />
        <Suspense fallback={null}>
          <InstallPrompt />
          <PWAInstallBanner />
          <PlatformDisclaimer />
          <EnhancedGDPRBanner />
          <QuickActionsWidget />
          <OfflineIndicator isOnline={isOnline} hasOfflineData={hasOfflineData} />
          <PWAStatus />
          <PWAUpdateNotification />
          {needRefresh && (
            <UpdatePrompt
              onUpdate={() => updateServiceWorker?.()}
              onDismiss={closePrompt}
            />
          )}
        </Suspense>
      </div>
    </EnhancedErrorBoundary>
  );
}

export default App;